import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
    BarChart3, Coins, FileText, Loader2, Percent, ReceiptIndianRupee, TrendingUp, Wallet,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardAPI } from "@/lib/api";

const PALETTE = ["#2563EB", "#059669", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#EF4444", "#14B8A6", "#F97316", "#6366F1"];
const STATUS_COLORS = {
    booked: "#64748B", in_progress: "#2563EB", awaiting_parts: "#F59E0B",
    awaiting_approval: "#8B5CF6", qc: "#06B6D4", ready: "#059669",
    delivered: "#16A34A", cancelled: "#94A3B8", unknown: "#CBD5E1",
};
const inr = (m) => `₹${Math.round((m || 0) / 100).toLocaleString("en-IN")}`;
const inrCompact = (m) => {
    const r = Math.round((m || 0) / 100);
    if (r >= 1e7) return `₹${(r / 1e7).toFixed(2)}Cr`;
    if (r >= 1e5) return `₹${(r / 1e5).toFixed(2)}L`;
    if (r >= 1e3) return `₹${(r / 1e3).toFixed(1)}k`;
    return `₹${r}`;
};
const dayLabel = (iso) => {
    const [, m, d] = (iso || "").split("-");
    return m && d ? `${d}/${m}` : iso;
};
const prettyStatus = (s) => (s || "").replace(/_/g, " ");

function ChartCard({ title, icon: Icon, testid, children, right }) {
    return (
        <Card data-testid={testid}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-zinc-100 py-3 dark:border-zinc-800">
                <CardTitle className="flex items-center gap-2 text-sm font-display font-bold">
                    {Icon && <Icon className="h-4 w-4 text-brand-blue" />}{title}
                </CardTitle>
                {right}
            </CardHeader>
            <CardContent className="p-4">{children}</CardContent>
        </Card>
    );
}

function EmptyChart({ label = "No data for this period." }) {
    return <div className="grid h-[240px] place-items-center text-sm text-zinc-400">{label}</div>;
}

function KpiCard({ label, value, sub, icon: Icon, testid }) {
    return (
        <Card data-testid={testid} className="flex flex-col gap-2 p-4">
            <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
                {Icon && <Icon className="h-4 w-4 text-zinc-400" />}
            </div>
            <div className="font-display text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">{value}</div>
            {sub && <div className="text-xs text-zinc-500">{sub}</div>}
        </Card>
    );
}

export function AnalyticsTab() {
    const { branches, currentBranchId, user } = useAuth();
    const [days, setDays] = useState(30);
    const [branchId, setBranchId] = useState(user?.role === "owner" ? "" : (currentBranchId || ""));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { days };
            if (branchId) params.branch_id = branchId;
            const d = await DashboardAPI.bi(params);
            setData(d);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [days, branchId]);

    useEffect(() => { load(); }, [load]);

    const trend = useMemo(
        () => (data?.revenue_trend || []).map((r) => ({ ...r, rupees: Math.round((r.revenue_minor || 0) / 100), label: dayLabel(r.date) })),
        [data]
    );
    const jobs = useMemo(() => (data?.jobs_by_status || []).map((j) => ({ ...j, label: prettyStatus(j.status) })), [data]);
    const payments = useMemo(() => (data?.payment_methods || []).map((p) => ({ ...p, label: prettyStatus(p.method), rupees: Math.round((p.amount_minor || 0) / 100) })), [data]);
    const topParts = useMemo(() => (data?.top_parts || []).map((p) => ({ ...p, rupees: Math.round((p.value_minor || 0) / 100) })), [data]);
    const topCustomers = useMemo(() => (data?.top_customers || []).map((c) => ({ ...c, rupees: Math.round((c.revenue_minor || 0) / 100) })), [data]);
    const byBranch = useMemo(() => (data?.revenue_by_branch || []).map((b) => ({ ...b, rupees: Math.round((b.revenue_minor || 0) / 100) })), [data]);

    const k = data?.kpis || {};
    const gst = data?.gst || {};

    return (
        <div className="space-y-6" data-testid="dashboard-analytics">
            {/* controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex rounded-md border border-zinc-200 p-0.5 dark:border-zinc-800" data-testid="bi-period-toggle">
                    {[7, 30, 90].map((d) => (
                        <Button
                            key={d}
                            size="sm"
                            variant={days === d ? "default" : "ghost"}
                            className={days === d ? "bg-brand-blue text-white hover:bg-brand-blueDeep" : ""}
                            onClick={() => setDays(d)}
                            data-testid={`bi-period-${d}`}
                        >
                            {d}d
                        </Button>
                    ))}
                </div>
                <Select value={branchId || "_all"} onValueChange={(v) => setBranchId(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[230px]" data-testid="bi-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All branches</SelectItem>
                        {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {loading && (
                <div className="grid h-[200px] place-items-center text-zinc-400" data-testid="bi-loading">
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            )}

            {!loading && !data && (
                <Card data-testid="bi-error">
                    <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                        <p className="text-sm text-zinc-500">Couldn't load analytics for this period.</p>
                        <Button size="sm" variant="outline" onClick={load} data-testid="bi-retry">Retry</Button>
                    </CardContent>
                </Card>
            )}

            {!loading && data && (
                <>
                    {/* KPI strip */}
                    <section className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                        <KpiCard testid="bi-kpi-revenue" label={`Revenue · ${days}d`} value={inrCompact(k.revenue_minor)} sub="Collected (paid + part)" icon={ReceiptIndianRupee} />
                        <KpiCard testid="bi-kpi-invoices" label="Invoices" value={k.invoices_issued ?? 0} sub={`Avg ${inrCompact(k.avg_invoice_minor)}`} icon={FileText} />
                        <KpiCard testid="bi-kpi-outstanding" label="Outstanding" value={inrCompact(k.outstanding_minor)} sub="Receivables" icon={Wallet} />
                        <KpiCard testid="bi-kpi-conversion" label="Est. conversion" value={`${k.estimate_conversion_pct ?? 0}%`} sub={`${k.estimates_converted ?? 0}/${k.estimates_total ?? 0} converted`} icon={Percent} />
                        <KpiCard testid="bi-kpi-gst" label="GST collected" value={inrCompact(gst.total_minor)} sub="CGST + SGST + IGST" icon={Coins} />
                    </section>

                    {/* Revenue trend */}
                    <ChartCard title={`Revenue trend · last ${days} days`} icon={TrendingUp} testid="bi-chart-revenue-trend">
                        {trend.some((t) => t.rupees > 0) ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2563EB" stopOpacity={0.35} />
                                            <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} interval="preserveStartEnd" minTickGap={24} />
                                    <YAxis tickFormatter={(v) => inrCompact(v * 100)} tick={{ fontSize: 11, fill: "#71717a" }} width={56} />
                                    <Tooltip formatter={(v) => [inr(v * 100), "Revenue"]} labelFormatter={(l) => `Date ${l}`} />
                                    <Area type="monotone" dataKey="rupees" stroke="#2563EB" strokeWidth={2} fill="url(#revGrad)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : <EmptyChart />}
                    </ChartCard>

                    {/* Jobs by status + Payment methods */}
                    <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <ChartCard title="Job cards by status" icon={BarChart3} testid="bi-chart-jobs-status">
                            {jobs.length ? (
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie data={jobs} dataKey="count" nameKey="label" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                            {jobs.map((j) => <Cell key={j.status} fill={STATUS_COLORS[j.status] || "#94A3B8"} />)}
                                        </Pie>
                                        <Tooltip formatter={(v, n) => [v, n]} />
                                        <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : <EmptyChart />}
                        </ChartCard>

                        <ChartCard title="Payment method mix" icon={Wallet} testid="bi-chart-payment-methods">
                            {payments.length ? (
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie data={payments} dataKey="rupees" nameKey="label" outerRadius={90} paddingAngle={2}>
                                            {payments.map((p, i) => <Cell key={p.method} fill={PALETTE[i % PALETTE.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v, n) => [inr(v * 100), n]} />
                                        <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : <EmptyChart label="No payments in this period." />}
                        </ChartCard>
                    </section>

                    {/* Top parts + Top customers */}
                    <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <ChartCard title="Top parts by consumption value" icon={BarChart3} testid="bi-chart-top-parts">
                            {topParts.length ? (
                                <ResponsiveContainer width="100%" height={Math.max(220, topParts.length * 38)}>
                                    <BarChart data={topParts} layout="vertical" margin={{ left: 8, right: 16 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                                        <XAxis type="number" tickFormatter={(v) => inrCompact(v * 100)} tick={{ fontSize: 11, fill: "#71717a" }} />
                                        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#71717a" }} />
                                        <Tooltip formatter={(v) => [inr(v * 100), "Value"]} />
                                        <Bar dataKey="rupees" fill="#059669" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyChart label="No parts consumed in this period." />}
                        </ChartCard>

                        <ChartCard title="Top customers by revenue" icon={Coins} testid="bi-chart-top-customers">
                            {topCustomers.length ? (
                                <ResponsiveContainer width="100%" height={Math.max(220, topCustomers.length * 38)}>
                                    <BarChart data={topCustomers} layout="vertical" margin={{ left: 8, right: 16 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                                        <XAxis type="number" tickFormatter={(v) => inrCompact(v * 100)} tick={{ fontSize: 11, fill: "#71717a" }} />
                                        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#71717a" }} />
                                        <Tooltip formatter={(v) => [inr(v * 100), "Revenue"]} />
                                        <Bar dataKey="rupees" fill="#2563EB" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyChart />}
                        </ChartCard>
                    </section>

                    {/* GST + Revenue by branch */}
                    <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <ChartCard title="GST collected" icon={Coins} testid="bi-chart-gst">
                            <div className="grid grid-cols-3 gap-3">
                                {[["CGST", gst.cgst_minor], ["SGST", gst.sgst_minor], ["IGST", gst.igst_minor]].map(([lbl, val]) => (
                                    <div key={lbl} className="rounded-md border border-zinc-200 p-3 text-center dark:border-zinc-800">
                                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{lbl}</div>
                                        <div className="mt-1 font-display text-lg font-bold tabular-nums" data-testid={`bi-gst-${lbl.toLowerCase()}`}>{inrCompact(val)}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 flex items-center justify-between rounded-md bg-brand-blueSoft px-3 py-2 dark:bg-brand-blue/10">
                                <span className="text-xs font-medium text-brand-blueDeep dark:text-brand-blue">Total tax collected</span>
                                <span className="font-display text-lg font-bold tabular-nums text-brand-blueDeep dark:text-brand-blue" data-testid="bi-gst-total">{inr(gst.total_minor)}</span>
                            </div>
                        </ChartCard>

                        <ChartCard title="Revenue by branch" icon={BarChart3} testid="bi-chart-revenue-branch">
                            {byBranch.length ? (
                                <ResponsiveContainer width="100%" height={Math.max(180, byBranch.length * 48)}>
                                    <BarChart data={byBranch} margin={{ left: 0, right: 16 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                                        <XAxis dataKey="code" tick={{ fontSize: 11, fill: "#71717a" }} />
                                        <YAxis tickFormatter={(v) => inrCompact(v * 100)} tick={{ fontSize: 11, fill: "#71717a" }} width={56} />
                                        <Tooltip formatter={(v) => [inr(v * 100), "Revenue"]} />
                                        <Bar dataKey="rupees" radius={[4, 4, 0, 0]}>
                                            {byBranch.map((b, i) => <Cell key={b.branch_id} fill={PALETTE[i % PALETTE.length]} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <EmptyChart />}
                        </ChartCard>
                    </section>
                </>
            )}
        </div>
    );
}
